import { Catalogue } from './catalogues';
import {
  ShiftType,
  EmailType,
  RoundOffOption,
  RoundOffValue,
  TipCalculation,
  ReceiptPrintOption,
  SignatureAndTippingOption,
  AppScreen,
  DeviceProfileStatus,
  PrinterType,
  PrinterBrand,
  PrinterSeries,
  KitchenDisplayMode,
  DeviceMode,
  OrderIdentifier,
  ThemeType,
  MediaType,
  Theme,
  OrbAutoClearTimeout,
} from '../enums';
import { FunctionMap } from './functionMaps';
import { Order } from './orders';
import { Address, Section, Venue } from './venues';
import { PrintingOptions } from './printerTemplate';
import { OrderType } from './organizations';
import { TimeBlock } from './schedule';
import { PricingGroup } from './pricing';
import { PrinterProfile, Product, Variant } from '.';
import { LayoutStrategyConfig } from '../inputs';

export interface GridSize {
  rows: number;
  columns: number;
}

export enum GridType {
  PRODUCTS = 'products',
  OPTIONS = 'options',
}

export interface EmailPreference {
  emailType: EmailType;
  isEnabled: boolean;
  emails?: string[];
}
export interface KdsNotificationConfig {
  notifyOnBump?: boolean;
  notifyOnReceive?: boolean;
  bumpMessage?: string;
  receiveMessage?: string;
}
export interface KdsTimerMilestone {
  minutes: number;
  label: string;
  color: string;
}

export interface InventoryOptions {
  allowSellingWithNegativeInventory: boolean;
}

export interface TipOption {
  name: string;
  value: number;
}

export const DEFAULT_TIP_OPTIONS: TipOption[] = [
  { name: 'Good', value: 5 },
  { name: 'Best', value: 10 },
  { name: 'Amazing!', value: 15 },
];

export interface CheckoutOptions {
  enableRoundOff: boolean;
  roundingMethod: RoundOffOption;
  roundOffValue: RoundOffValue;
  enableTips: boolean;
  tipCalculation: TipCalculation;
  withdrawTipAtClosureTime: boolean;
  showQRPay?: boolean;
  tipOptions?: TipOption[];
}

export interface OperatingHours {
  id?: string;
  name?: string;
  timeBlocks: TimeBlock[];
}

export interface ProfitMargin {
  productTypeId: string;
  margin: number;
}

export interface PayStoreRef {
  online?: string;
  instore?: string;
}

export interface Store {
  id: string;
  name: string;
  venue: Venue;
  emailPreferences: EmailPreference[];
  inventoryOptions: InventoryOptions;
  shiftType: ShiftType;
  checkoutOptions: CheckoutOptions;
  printers: Printer[];
  deviceProfiles: DeviceProfile[];
  devices: Device[];
  isDeleted: boolean;
  operatingHours: OperatingHours;
  profitMargins: ProfitMargin[];
  catalogues: Catalogue[];
  pricingGroups: PricingGroup[];
  slug?: string;
  showHourlySplit?: boolean;
  showStaffStats?: boolean;
  merchantCode?: string;
  merchantId?: string;
  address?: Address;
  businessIdentifier?: string;
  email?: string;
  phoneNumber?: string;
  timezone?: string;
  payStoreRefs?: PayStoreRef;
  enableLocalComms?: boolean;
  priceListId?: string;
  products?: Product[];
  variants?: Variant[];
}
export interface Printer {
  id: string;
  name: string;
  code?: string;
  brand?: PrinterBrand;
  ipAddress?: string;
  port?: number;
  bdAddress?: string;
  slug?: string;
  series?: PrinterSeries;
  printerType: PrinterType;
  isKOT: boolean;
  store: Store;
  isActive: boolean;
  fallback?: {
    autoRedirectEnabled: boolean;
    printers: FallbackPrinter[];
  };
  deviceId?: string;
  serialPort?: string;
  serialBaud?: number;
  serialParity?: string;
  serialDataBits?: number;
  usbVendorId?: string;
  usbProductId?: string;
  usbSerialNumber?: string;
  chunkSize?: number;
}

export interface FallbackPrinter {
  priority: number;
  printer: Printer;
}

export interface PrinterOptions {
  printReceipt: boolean;
  emailReceipt: boolean;
}

export interface DisplayAsset {
  position: number;
  uri: string;
}

export interface DisplayScreen {
  mediaType?: MediaType;
  logoUri?: string;
  backgroundUri?: string;
  assets?: DisplayAsset[];
}

export interface DeviceProfileBrandingHeader {
  color?: string;
  uri?: string;
}

export interface DeviceProfileBranding {
  brandColor?: string;
  theme?: ThemeType;
  header?: DeviceProfileBrandingHeader;
  idleScreen?: DisplayScreen;
  cartScreen?: DisplayScreen;
}

export interface DeviceProfile {
  id: string;
  name: string;
  store: Store;
  menu: Catalogue;
  functionMap: FunctionMap;
  orderTypes: OrderType[];
  receiptPrintOption: ReceiptPrintOption;
  printerOptions: PrinterOptions;
  lockScreenTimeout: number;
  enableAudio: boolean;
  signatureAndTipping: SignatureAndTippingOption;
  enableCashManagement: boolean;
  enableFloorView: boolean;
  enableAllergens: boolean;
  enableBarcodeScan: boolean;
  enableCollapseSentItems: boolean;
  hideCartTotal: boolean;
  hideSendButton: boolean;
  showCartKeypad: boolean;
  autoRestartHour?: number;
  defaultSaleScreen: AppScreen;
  postSaleScreen: AppScreen;
  status: DeviceProfileStatus;
  isDeleted: boolean;
  defaultOrderType: OrderType;
  isActive: boolean;
  isDefault: boolean;
  devices: Device[];
  sections: Section[];
  enableCourses: boolean;
  enableSeatManagement: boolean;
  enableWaiterAssignment: boolean;
  enableHideZeroPrices: boolean;
  enableTareWeight: boolean;
  enablePinOnlyLogin: boolean;
  enableComplimentaryMode: boolean;
  enableOnlineOrders: boolean;
  enableQRPrint: boolean;
  enableKitchenBuzzer: boolean;
  orgLogo?: string;
  backgroundImage?: string;
  promoImages?: string[];
  showPrinterProfileInDocket?: boolean;
  printZeroValueItems?: boolean;
  printZeroValueModifiers?: boolean;
  printNotes?: boolean;
  mode?: DeviceMode;
  pricingGroupId?: string;
  orderIdentifier?: OrderIdentifier;
  promptGuestCount?: boolean;
  takingOrders?: boolean;
  payAtTable?: boolean;
  enableDarkMode?: boolean;
  showPriceOnTiles?: boolean;
  productsGridSize?: GridSize;
  optionsGridSize?: GridSize;
  customerDisplay?: DeviceProfileBranding;
  branding?: DeviceProfileBranding;
  showStatusBar?: boolean;
  showHoldBtn?: boolean;
  maxSplitPayments?: number;
  hideSplitByProduct?: boolean;
  hideSplitBySeat?: boolean;
  hideQuickCashOptions?: boolean;
}

export interface TokenSettings {
  tokenRange?: {
    start?: number;
    end?: number;
  };
  resetTokenNumberOnShiftClosure?: boolean;
  onlyPrintTokenOnReceipts?: boolean;
  // For kiosk device
  resetTokenNumberOnEndOfDay?: boolean;
}

export interface Device {
  id: string;
  name: string;
  details: string;
  uuid?: string;
  isPaired?: boolean;
  deviceCode?: string;
  appVersion: string;
  store: Store;
  deviceProfile: DeviceProfile;
  salesPrefix: string;
  returnPrefix: string;
  cashDrawer: string;
  paymentTerminal: Terminal;
  previousOrder?: Order;
  printingOptions?: PrintingOptions[];
  pricingGroupId?: string;
  pushNotificationToken?: string;
  isOnline?: boolean;
  lastDisconnectedAt: number;
  customerDisplay?: CustomerDisplay;
  poiId?: string;
  lastOrderNumber?: string;
  mode: DeviceMode;
  disablePolling?: boolean;
  enablePaycloudPayments?: boolean;
  enableDeeplinkPayments?: boolean;
  payCloudConfig?: { terminalId?: string };
  isTokenNumberActive?: boolean;
  tokenSettings?: TokenSettings;
  trainingMode?: boolean;
  currentTokenNumber?: number;
  useCloudPayment?: boolean;
  linkCdsToPayApp?: boolean;
  config?: DeviceConfig;
  deviceMetadata?: DeviceMeta;
  configVersion?: number;
}

export interface DeviceMeta {
  browser?: string;
  os?: string;
  appVersion?: string;
  userAgent?: string;
  platform?: string;
  deviceType?: string;
  screenResolution?: string;
  timezone?: string;
}

export interface OrbDeviceConfig {
  type: DeviceMode;
  orderTypeIds?: string[]; //can add orderType[] field later
  printerProfileIds?: string[]; //can add PrinterProfile[] field later
  autoClearTimeout?: OrbAutoClearTimeout;
  theme?: Theme;
}

export type DeviceConfig = OrbDeviceConfig | null;

export interface Terminal {
  id: string;
  name: string;
  uuid: string;
  store: Store;
  device: Device;
  ipAddress?: string;
  enableLocalPayment?: boolean;
}

export interface CustomerDisplay {
  id: string;
  name: string;
  deviceUuid?: string;
  isPaired?: boolean;
  deviceCode?: string;
  lastLoginAt?: number;
  connectedPosDevice?: Device;
}

export interface KitchenDisplay {
  id: string;
  name: string;
  details: string;
  uuid?: string;
  isPaired?: boolean;
  deviceCode?: string;
  store: Store;
  printerProfiles?: PrinterProfile[];
  lastDisconnectedAt: number;
  mode: KitchenDisplayMode;
  notificationConfig: KdsNotificationConfig;
  timerMilestones?: KdsTimerMilestone[];
  overtimeMinutes?: number;
  printer?: Printer;
  autoPrintOnCompletion?: boolean;
  singleItemPrinting?: boolean;
  layoutStrategy?: LayoutStrategyConfig;
  groupItemsByStatus?: boolean;
  isCourseCallEnabled?: boolean;
}
